import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import CircleStyle from "ol/style/Circle";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { Draw } from "ol/interaction";
import { toLonLat } from "ol/proj";
import type { MeasureMode } from "../types";

export default async function initOLMeasure(map: any) {
  const source = new VectorSource();
  const layer = new VectorLayer({ source });
  layer.set("id", "measure-layer");

  map.addLayer(layer);

  const draw = new Draw({
    source,
    type: "LineString",
  });

  map.addInteraction(draw);

  return draw;
}

export function createOLMeasure(map: any, mode: MeasureMode) {
  let draw: any;

  const source = new VectorSource();
  const layer = new VectorLayer({ source });
  layer.set("id", "measure-layer");

  map.addLayer(layer);

  const style = new Style({
    fill: new Fill({
      color: "rgba(255, 255, 255, 0.2)",
    }),
    stroke: new Stroke({
      color: "rgba(0, 0, 0, 0.5)",
      lineDash: [10, 10],
      width: 2,
    }),
    image: new CircleStyle({
      radius: 5,
      stroke: new Stroke({
        color: "rgba(0, 0, 0, 0.7)",
      }),
      fill: new Fill({
        color: "rgba(255, 255, 255, 0.2)",
      }),
    }),
  });

  return {
    activate() {
      draw = new Draw({
        source: source,
        type: mode === "DISTANCE" ? "LineString" : "Polygon",
        style: function (feature) {
          const geometryType = feature.getGeometry()?.getType();
          if (geometryType === (mode === "DISTANCE" ? "LineString" : "Polygon") || geometryType === "Point") {
            return style;
          }
        },
      });
      map.addInteraction(draw);
    },

    deactivate() {
      if (draw) {
        map.removeInteraction(draw);
      }
    },
  };
}

export function calculateBearing(coord1: number[], coord2: number[]) {
  const [lon1, lat1] = toLonLat(coord1);
  const [lon2, lat2] = toLonLat(coord2);

  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

  const θ = Math.atan2(y, x);

  return ((θ * 180) / Math.PI + 360) % 360;
}

const R = 6378137;

export function toRad(d: number) {
  return (d * Math.PI) / 180;
}

export function haversine(lat1: number, lon1: number, lat2: number, lon2: number) {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

  return 2 * R * Math.asin(Math.sqrt(a));
}

export function calculateAzimuth(lat1: number, lon1: number, lat2: number, lon2: number) {
  const y = Math.sin(toRad(lon2 - lon1)) * Math.cos(toRad(lat2));
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lon2 - lon1));

  return (Math.atan2(y, x) * 180) / Math.PI;
}
